import { useCallback, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { invalidation, isApiError, message } from '@sl/core';
// The modules, not the barrels: the shell must not pull the tree table or the search palette into its chunk.
import { clearChartColorsFor } from '../../charts/chartColors/clearChartColors';
import { clearExpansionFor } from '../../tree/clearExpansion';
import { clearRecent } from '../../search/recent';
import { Button } from '../../ui/Button/Button';
import { Modal } from '../../ui/Modal/Modal';
import { useToast } from '../../ui/Toast/ToastProvider';
import { useApiClient } from '../api/ApiClientProvider';
import { messageText } from '../messages';
import { testId } from '../testId';
import { SHELL_COPY } from './copy';
import { SurfaceModeChip } from './ModeChip';
import { useShell } from './shellContext';

// "START FRESH" (REQ-001 AC6) — one flow, raised from two places: the mode menu (where the demo controls
// are) and Settings (where a demo account's two ways back sit beside each other).
//
// D-114 parked it and D-128 took the phone's dead control out (BUG-029, T-242) under the rule that neither
// build draws it until it works. It works now: `client.startFresh` applies the `start_fresh` seed — no plan,
// no guardrail, no rules, an empty inbox, nothing staged, first-run unseen — and `invalidation.startFresh`
// drops everything under the account plus the session's account list, so every screen re-reads. The two
// things a demo remembers OUTSIDE its own namespace are forgotten by hand, exactly as "Reset demo" does:
// which tree rows were open (D-097 (7)) and the last five things you opened from search (T-044's note (6)).
//
// Afterwards the app lands on Home. There is no first-run screen on the web (`routes/welcome/` is the
// signed-out landing page, not first-run), so Home with no plan, no alerts and nothing staged IS the
// brand-new account; REQ-005's first-run cards arrive with T-176 and will take over that landing without
// changing this flow.
//
// The ids are the five T-242 wrote down so they could come back with the same spellings: `mode.start-fresh`
// (the mode menu's trigger) and `shell.start-fresh` with `.confirm` / `.cancel` / `.mode-chip`. Settings
// raises the same dialog from `settings.start-fresh`.

export interface StartFreshDialogProps {
  open: boolean;
  onClose: () => void;
  /** Focus returns here when the dialog closes — the control that raised it. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function StartFreshDialog({ open, onClose, returnFocusRef }: StartFreshDialogProps) {
  const { account, session, messages } = useShell();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [working, setWorking] = useState(false);
  // 8 px of daylight between a destructive action and the safe one is the DS rule (§13.5); focus lands on
  // the safe one, because this dialog is the last thing between a demo and an empty account.
  const cancelRef = useRef<HTMLButtonElement>(null);

  const run = useCallback(async () => {
    setWorking(true);
    try {
      await apiClient.startFresh(account.id);
      await clearExpansionFor({ userId: session.user.id, accountId: account.id });
      // REQ-025 AC8: the chart's colour settings go with everything else "Start fresh" clears.
      await clearChartColorsFor({ accountId: account.id });
      clearRecent(account.id);
      await queryClient.invalidateQueries(invalidation.startFresh(account.id));
      onClose();
      // One announcement, through the shell's single live region (T-035).
      toast({ message: SHELL_COPY.startFreshDone, tone: 'success', key: 'shell.start-fresh' });
      void navigate('/home');
    } catch (error) {
      const sentence = isApiError(error) ? error.userMessage : message('error.unknown');
      toast({ message: messageText(sentence, messages), tone: 'error' });
    } finally {
      setWorking(false);
    }
  }, [apiClient, account.id, session.user.id, queryClient, onClose, toast, navigate, messages]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      role="alertdialog"
      size="sm"
      dismissible={!working}
      title={SHELL_COPY.startFreshTitle}
      subtitle={SHELL_COPY.startFreshBody}
      initialFocusRef={cancelRef}
      returnFocusRef={returnFocusRef}
      testID={testId('shell', 'start-fresh')}
      /* REQ-006 AC5 / BUG-009: a centred dialog covers the top bar at 360 px, and this one is about to take
         the demo's whole story away — the mode belongs in front of you while you decide. */
      modeChip={<SurfaceModeChip testID={testId('shell', 'start-fresh', 'mode-chip')} />}
      footer={
        <>
          <Button
            ref={cancelRef}
            variant="ghost"
            disabled={working}
            testID={testId('shell', 'start-fresh', 'cancel')}
            onClick={onClose}
          >
            {SHELL_COPY.startFreshCancel}
          </Button>
          <Button
            variant="danger"
            loading={working}
            testID={testId('shell', 'start-fresh', 'confirm')}
            onClick={() => void run()}
          >
            {working ? SHELL_COPY.startFreshWorking : SHELL_COPY.startFreshConfirm}
          </Button>
        </>
      }
    />
  );
}
